export type ModeId =
  | 'keyboard'
  | 'mouse'
  | 'direct-bot'
  | 'simulated-human'
  | 'physical-button'
  | 'emg'
  | 'hhi-human';

export interface ModeDefinition {
  id: ModeId;
  label: string;
  /** One line, shown under the mode selector. */
  description: string;
  /** The bot generates intent in this mode. */
  usesBot: boolean;
  /** Intent is mediated through a stimulator in this mode. */
  usesStimulation: boolean;
  /** Needs hardware the operator must attach and connect. */
  requiresHardware: boolean;
  /** Can drive real electrodes. Gets the loud banner and the extra confirmation. */
  canStimulateAPerson: boolean;
}

/**
 * The experimental modes.
 *
 * They differ in exactly two dimensions -- where intent comes from, and what
 * turns intent into a flap -- and the table below is the whole of that:
 *
 *   mode              intent      actuator                     flap comes from
 *   ----------------  ----------  ---------------------------  ------------------
 *   keyboard          human       none                         keydown
 *   mouse             human       none                         pointerdown
 *   direct-bot        bot         none                         the decision itself
 *   simulated-human   bot         SimulatedHumanStimulator     virtual button
 *   physical-button   human       none                         real switch
 *   emg               human       none                         EMG threshold
 *   hhi-human         bot         BackyardBrainsHHIAdapter     real switch / EMG
 *
 * The last row is the experiment. Every other row exists to make it possible to
 * build, debug, and baseline that row without a person attached.
 */
export const MODES: ModeDefinition[] = [
  {
    id: 'keyboard',
    label: 'Keyboard',
    description: 'A human plays normally with Space. Escape is the emergency stop.',
    usesBot: false,
    usesStimulation: false,
    requiresHardware: false,
    canStimulateAPerson: false,
  },
  {
    id: 'mouse',
    label: 'Mouse / touch',
    description: 'A human plays by clicking or tapping the play field.',
    usesBot: false,
    usesStimulation: false,
    requiresHardware: false,
    canStimulateAPerson: false,
  },
  {
    id: 'direct-bot',
    label: 'Direct bot',
    description: 'The bot controls the flap directly. Intent and execution are the same event.',
    usesBot: true,
    usesStimulation: false,
    requiresHardware: false,
    canStimulateAPerson: false,
  },
  {
    id: 'simulated-human',
    label: 'Simulated human',
    description:
      'Bot → simulated stimulation → synthetic response latency → virtual button → flap. ' +
      'No hardware, no participant; the full mediated pipeline.',
    usesBot: true,
    usesStimulation: true,
    requiresHardware: false,
    canStimulateAPerson: false,
  },
  {
    id: 'physical-button',
    label: 'Physical button',
    description: 'An external switch flaps the bird. No bot, no stimulation.',
    usesBot: false,
    usesStimulation: false,
    requiresHardware: true,
    canStimulateAPerson: false,
  },
  {
    id: 'emg',
    label: 'EMG',
    description: 'A muscle contraction crossing threshold flaps the bird. No bot, no stimulation.',
    usesBot: false,
    usesStimulation: false,
    requiresHardware: true,
    canStimulateAPerson: false,
  },
  {
    id: 'hhi-human',
    label: 'HHI human (real stimulation)',
    description:
      'Bot → HHI stimulation request → participant’s arm moves → switch or EMG → flap. ' +
      'Connect the controller, then click START GAME.',
    usesBot: true,
    usesStimulation: true,
    requiresHardware: true,
    canStimulateAPerson: true,
  },
];

export function getMode(id: ModeId): ModeDefinition {
  const mode = MODES.find((m) => m.id === id);
  if (!mode) throw new Error(`Unknown mode: ${id}`);
  return mode;
}

/** Presets for the delay conditions in the comparison table. */
export const DELAY_PRESETS: { label: string; meanLatencyMs: number; latencyStdDevMs: number }[] = [
  { label: '50 ms clean', meanLatencyMs: 50, latencyStdDevMs: 0 },
  { label: '100 ms clean', meanLatencyMs: 100, latencyStdDevMs: 0 },
  { label: '150 ms clean', meanLatencyMs: 150, latencyStdDevMs: 0 },
  { label: '80 ± 20 ms', meanLatencyMs: 80, latencyStdDevMs: 20 },
  { label: '150 ± 30 ms', meanLatencyMs: 150, latencyStdDevMs: 30 },
];
